import React, { useState } from "react";
import { Col, Form, Popconfirm, Row, Switch, notification } from "antd";
import api from "../../services/api";
import { WEB_FLOW } from "../../constants/station";
import { useFreire } from "../../utils/freireContext";
import * as texts from "../../pages/Companies/locales";
import {
  filterStationsByCompanyIds,
  getCompanyScopeIds,
  groupStationIdsByCountry,
} from "./helpers";

interface CompanyWebFlowBatchProps {
  company?: { _id?: string } | null;
  allCompanies?: { _id: string; parentCompanyID?: string | null }[];
}

export const CompanyWebFlowBatch: React.FC<CompanyWebFlowBatchProps> = ({
  company,
  allCompanies = [],
}) => {
  const { freire } = useFreire();
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [pendingValue, setPendingValue] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [stationIdsByCountry, setStationIdsByCountry] = useState<
    Record<string, string[]>
  >({});

  // Nothing to batch update while the company is still being created.
  if (!company?._id) return null;

  const companyIds = getCompanyScopeIds(company as { _id: string }, allCompanies);

  const affectedCount = Object.values(stationIdsByCountry).reduce(
    (total, ids) => total + ids.length,
    0
  );

  const notifyError = (error: any) => {
    const description =
      error?.response?.data?.message ??
      error?.message ??
      freire(texts.WEB_FLOW_GATEWAY_ERROR);

    notification.error({
      message: freire(texts.OOPS),
      description,
    });
  };

  const reset = () => {
    setConfirmVisible(false);
    setStationIdsByCountry({});
  };

  /**
   * Loads the stations first so the confirmation can state how many QR codes
   * are about to change, instead of asking the user to confirm blind.
   */
  const prepare = async (nextValue: boolean) => {
    // The confirmation renders over the switch, so ignore clicks while it is up.
    if (loading || confirmVisible) return;

    setLoading(true);

    try {
      const { data } = await api.get("select/connected_stations", {
        params: { companies: companyIds },
      });

      const scoped = filterStationsByCompanyIds(data, companyIds);
      const byCountry = groupStationIdsByCountry(scoped);

      if (!Object.keys(byCountry).length) {
        notification.warning({
          message: freire(texts.OOPS),
          description: freire(texts.WEB_FLOW_GATEWAY_NO_STATIONS),
        });
        return;
      }

      setPendingValue(nextValue);
      setStationIdsByCountry(byCountry);
      setConfirmVisible(true);
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setConfirmVisible(false);
    setLoading(true);

    // `disabled` is the schema default and the only value that sends the driver
    // back to the app, so it is the real inverse of `redirectToGateway`.
    const webFlow = pendingValue
      ? WEB_FLOW.redirectToGateway
      : WEB_FLOW.disabled;

    const countries = Object.keys(stationIdsByCountry);
    const applied: string[] = [];
    let updated = 0;

    try {
      // The batch endpoint rejects a payload mixing countries, so each country
      // goes in its own request.
      for (const country of countries) {
        const stationIds = stationIdsByCountry[country];

        await api.put("/batch-update/stations", {
          country,
          stationIds,
          web_flow: webFlow,
        });

        applied.push(country);
        updated += stationIds.length;
      }

      setEnabled(pendingValue);

      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: `${updated} ${freire(texts.WEB_FLOW_GATEWAY_SUCCESS)}`,
      });
    } catch (error) {
      // The countries handled before the failure stay applied, so naming them
      // keeps the operator from assuming the whole batch rolled back.
      if (updated > 0) {
        notification.warning({
          message: freire(texts.WEB_FLOW_GATEWAY_PARTIAL_TITLE),
          description: `${updated}/${affectedCount} ${freire(
            texts.WEB_FLOW_GATEWAY_PARTIAL
          )} ${applied.join(", ")}.`,
        });
      }

      notifyError(error);
    } finally {
      setStationIdsByCountry({});
      setLoading(false);
    }
  };

  return (
    <Row gutter={[16, 0]} style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form.Item
          label=""
          extra={
            <span>
              {freire(texts.WEB_FLOW_GATEWAY_DESCRIPTION)}
              <br />
              {freire(texts.WEB_FLOW_GATEWAY_BATCH_NOTE)}
            </span>
          }
        >
          <Popconfirm
            // The switch click is what starts the flow, so the confirmation is
            // opened from `prepare` instead of by the default click trigger.
            // rc-tooltip takes a list of actions and an empty one disables them
            // all; antd 3 only types a single action, hence the cast.
            trigger={[] as unknown as "click"}
            visible={confirmVisible}
            title={
              <div style={{ maxWidth: 280 }}>
                <div>
                  {`${affectedCount} ${freire(
                    texts.WEB_FLOW_GATEWAY_AFFECTED_STATIONS
                  )}`}
                </div>
                <div>{freire(texts.WEB_FLOW_GATEWAY_CONFIRM)}</div>
              </div>
            }
            okText={freire(texts.YES)}
            cancelText={freire(texts.NO)}
            onConfirm={apply}
            onCancel={reset}
          >
            <Switch
              checkedChildren={freire(texts.YES)}
              unCheckedChildren={freire(texts.NO)}
              loading={loading}
              checked={enabled}
              onChange={prepare}
            />
          </Popconfirm>
          <span style={{ marginLeft: 10 }}>
            {freire(texts.WEB_FLOW_GATEWAY_LABEL)}
          </span>
        </Form.Item>
      </Col>
    </Row>
  );
};

export default CompanyWebFlowBatch;
